import React, {
  memo,
  useState,
  useCallback,
  MouseEvent,
  ChangeEvent,
} from 'react';
import { connect, MapDispatchToProps } from 'react-redux';
import * as FileUtils from 'utils/file';
import { isInWhiteList } from 'utils/fileUtils';
import { MenuAction } from 'store/entity/types';

import {
  ActionMenuButton,
  HeaderContainer,
  MenuItemUploadFile,
  NavHeaderContent,
  NavHeaderItem,
} from './styles';
import Dropdown from 'components/Dropdown';
import Icon from 'components/Icon';
import LocalIcon from 'ui/LocalIcon';
import {
  IconColorFinder,
  IconLogoFinder,
} from '../../../../../utils/check-file-type/findIconViaType';
import ChangeLimitationModal from '../ChangeLimitationModal';
import notify from '../../../../../components/Notify';
import {
  createNewProviderItem,
  creatingNewProviderItem,
  creatingNewProviderItemFn,
  importContentFromRemote,
  importContentFromRemoteFn,
} from 'store/providers/providerSlice';
import { black, blue, orange } from '../../../../../utils/style/colors';
import { notifyError } from '../../../../../components/Notify/Notify';
import { readClipboard } from '../../../../../utils/happyFunctions';
import { getDomainAndPort } from '../../../../../api/Utils';
import { appDispatch } from 'store/store';
import {
  IProviderItemViewWithData,
  ITagView,
  ITEM_MIN_ORDER,
  providerItemType,
  ProviderItemType,
} from 'store/providers/providerTypes';
import { updateSearchInFolderId } from 'store/app/appSlice';
import { useTranslation, WithTranslation } from 'react-i18next';
import i18n, { languageDirection } from '../../../../../i18n';

const { Menu, MenuDivider, MenuItem } = Dropdown;

const { domainFeatures } = getDomainAndPort();

type DispatchProps = {
  updateFolderIdForSearch: (folderId: number) => void;
  importContentFromRemote: (
    sourceItemId: number,
    targetItemId: number,
    remoteUrl: string,
    readAccessToken: string,
    forceCopy: boolean,
    excludeSrcFolder: boolean,
    providerId: number
  ) => ReturnType<typeof importContentFromRemoteFn>;
  createProviderItem: (
    providerId: number,
    id: number,
    type: ProviderItemType,
    tags: ITagView[] | undefined
  ) => void;
  creatingProviderItem: (
    providerId: number,
    dadId: number,
    dataUrl: string,
    fileSize: any,
    displayName: string,
    format: any,
    photo: any,
    order: number,
    originalFileName: any,
    error: any
  ) => ReturnType<typeof creatingNewProviderItemFn>;
};

type OwnProps = {
  item: IProviderItemViewWithData;
  limitAccessToCompany: boolean;
  userIsOwnerOrSupport: boolean;
  userIsAdvancedAdmin: boolean;
  managingCompanyId?: number;
  onPasteItems?: (e: MouseEvent<HTMLElement>) => void | undefined;
  goUp?: () => void;
  isRoot?: boolean;
  showingDeleted?: boolean;
  setShowDeleted?: (value: boolean) => void;
};

type ComponentProps = OwnProps & DispatchProps & WithTranslation;

const RastechCompany = 11;
const FolderHeader: React.FC<ComponentProps> = ({
  createProviderItem,
  creatingProviderItem,
  item,
  limitAccessToCompany,
  managingCompanyId,
  onPasteItems,
  userIsOwnerOrSupport,
  userIsAdvancedAdmin,
  goUp,
  importContentFromRemote,
}) => {
  const { data: dataItem } = item;
  const actionWidth = '48px';

  const [isLimitationModalOpen, setIsLimitationModalOpen] =
    useState<boolean>(false);
  const { t } = useTranslation('documentManager');
  const [openMoreHorizontalDrp, setOpenMoreHorizontalDrp] = useState(false);
  const handleMoreHorizontalDrpShowChange = useCallback(
    (show: boolean) => {
      setOpenMoreHorizontalDrp(show);
    },
    [setOpenMoreHorizontalDrp]
  );

  const [openPlusDrp, setOpenPlusDrp] = useState(false);
  const handlePlusDrpShowChange = useCallback((show: boolean) => {
    setOpenPlusDrp(show);
  }, []);

  const handleOnPasteItems = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (onPasteItems) {
        onPasteItems(e);
      }
      setOpenMoreHorizontalDrp(false);
    },
    [onPasteItems]
  );

  const handleMenuCallback = useCallback(
    (
      e: MouseEvent<HTMLElement>,
      callback: (e: MouseEvent<HTMLElement>) => void
    ) => {
      callback(e);
      setOpenMoreHorizontalDrp(false);
    },
    []
  );
  const showLimitationModal = () => {
    setIsLimitationModalOpen(
      (preIsLimitationModalOpen) => !preIsLimitationModalOpen
    );
  };

  const handleShowLimitationModal = useCallback(showLimitationModal, [
    setIsLimitationModalOpen,
  ]);

  const handleCreatingNewItem = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const dataset: DOMStringMap = e.currentTarget.dataset;
    let type = dataset.itemType;
    let tags: ITagView[] = [];
    if (type === 'checklist') {
      type = 'survey';
      tags = [{ text: 'چک‌لیست' } as any];
    }
    if (type === 'indicator') {
      type = 'survey';
      tags = [{ text: 'شاخص' } as any];
    }
    createProviderItem(
      dataItem.providerId,
      dataItem.id,
      type as ProviderItemType,
      tags
    );
    setOpenPlusDrp(false);
  };

  // const handleSearch = (e: MouseEvent<HTMLElement>) => {
  //   e.preventDefault();
  //   const folderId = dataItem.id;
  //   updateFolderIdForSearch(folderId);
  // };

  const handleUploadingNewItem = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setOpenPlusDrp(false);
    const {
      target: { files },
    } = e;
    if (files) {
      for (let i = 0; i < files.length; i++) {
        const fileReader = new FileReader();
        fileReader.onload = () => {
          const fileUrl = URL.createObjectURL(files[i]);
          const fileType = FileUtils.detectFileType(fileReader, files[i]);
          const analyseFileType = FileUtils.analyseFileType(fileType);
          let displayName = files[i].name;
          const dotIndex = displayName.lastIndexOf('.');
          displayName = displayName.substring(0, dotIndex);
          let error;
          if (!isInWhiteList(files[i].type)) {
            error = { data: { message: 'canNotViewInMobile' } };
          }
          creatingProviderItem(
            dataItem.providerId,
            dataItem.id,
            fileUrl,
            files[i].size,
            displayName,
            analyseFileType.type,
            analyseFileType.isPhoto,
            ITEM_MIN_ORDER,
            files[i].name,
            error
          );
        };
        fileReader.readAsArrayBuffer(files[i].slice(0, 20));
      }
    }
  };

  const copyContent = (srcInfo: any, useInternalIfPossible: boolean) => {
    notify(t('copingItems'));
    const sameDomain =
      useInternalIfPossible &&
      srcInfo.remoteUrl.startsWith(getDomainAndPort().protocolDomain);
    const sameProvider =
      sameDomain && item.data.providerId === dataItem.providerId;
    importContentFromRemote(
      srcInfo.sourceItemId,
      item.data.id,
      sameDomain ? null : srcInfo.remoteUrl,
      srcInfo.readAccessToken,
      !sameProvider,
      srcInfo.excludeSrcFolder,
      item.data.providerId
    ).then(
      (res: any) => {
        notify(t('itemCopied', { count: res.message }));
      },
      (err: any) => {
        notifyError(err);
      }
    );
  };
  const menuActions: MenuAction[] = [
    {
      message: t('changeLimitation'),
      callBack: (e: MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        handleShowLimitationModal();
      },
      order: 1,
    },
  ];
  if (userIsOwnerOrSupport) {
    menuActions.push({
      message: t('pastCopiedFiles'),
      callBack: (e: MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        readClipboard((text) => {
          try {
            const content = JSON.parse(text);
            const found =
              content &&
              content.sourceItemId &&
              content.remoteUrl &&
              content.readAccessToken;
            if (found) {
              const result = window.confirm(
                t('copyContent', {
                  name: content.name,
                  domain: content.remoteUrl,
                })
              );
              if (result) copyContent(content, e.shiftKey);
            } else {
              notify(t('noItemFound'), 'warning');
            }
          } catch (e) {
            notify(t('noItemFound'), 'warning');
          }
        }, t('pleaseEnterInputHere'));
      },
      order: 2,
    });
    // if (isRoot)
    //   menuActions.push({
    //     message: showingDeleted
    //       ? messages.showingDeletedFiles
    //       : messages.showDeleted,
    //     callBack: (e: MouseEvent<HTMLElement>) => {
    //       e.preventDefault();
    //       e.stopPropagation();
    //       setShowDeleted && setShowDeleted(true);
    //     },
    //     order: 2,
    //   });
  }

  if (dataItem.type === providerItemType.folder) {
    return (
      <HeaderContainer>
        {isLimitationModalOpen && (
          <ChangeLimitationModal
            item={item}
            onComplete={handleShowLimitationModal}
            managingCompanyId={managingCompanyId}
            limitAccessToCompany={limitAccessToCompany}
          />
        )}
        <nav style={{ height: '5rem' }}>
          <NavHeaderContent className="nav-wrapper">
            <ul
              style={{
                flex: '1',
                maxWidth: 'calc(100% - ' + actionWidth + ')',
                display: 'flex',
              }}
            >
              {goUp && (
                <li>
                  <div
                    style={{
                      width: actionWidth,
                      overflow: 'hidden',
                      borderRadius: '1rem',
                    }}
                  >
                    <div
                      style={{
                        width: '200px',
                        float: languageDirection() === 'rtl' ? 'right' : 'left',
                        direction: languageDirection(),
                        padding: '0',
                        cursor: 'pointer',
                      }}
                      onClick={goUp}
                    >
                      <span style={{ display: 'none' }} />
                      <Icon
                        type="arrow-up"
                        color={black}
                        style={{ width: '48px' }}
                      />
                    </div>
                  </div>
                </li>
              )}
              <NavHeaderItem>
                <span
                  className="text"
                  style={{
                    maxHeight: '3.6rem',
                    overflow: 'hidden',
                    margin: 'auto',
                  }}
                >
                  {dataItem.dadId === null && !dataItem.name ? (
                    <Icon type={'home'} />
                  ) : (
                    dataItem.name
                  )}
                </span>
              </NavHeaderItem>
            </ul>
            <ul>
              {/*<li>
                <div
                  style={{
                    width: actionWidth,
                    overflow: 'hidden',
                  }}
                >
                  <a
                    href=""
                    style={{
                      width: '200px',
                      float: 'left',
                      direction: 'ltr',
                      padding: '0',
                    }}
                    onClick={handleSearch}
                  >
                    <span style={{ display: 'none' }} />
                    <Icon
                      type="search"
                      style={{ width: '48px' }}
                      color={black}
                    />
                  </a>
                </div>
              </li>*/}
              {dataItem.editable && (
                <>
                  <li>
                    <Dropdown
                      marginToTarget={0}
                      placement="bottom-start"
                      show={openMoreHorizontalDrp}
                      width={200}
                      onShowChange={handleMoreHorizontalDrpShowChange}
                      target={
                        <ActionMenuButton>
                          <Icon type="more-horizontal" color={black} />
                        </ActionMenuButton>
                      }
                    >
                      <Menu>
                        {onPasteItems && (
                          <MenuItem>
                            <a onClick={handleOnPasteItems} href="">
                              {t('paste')}
                            </a>
                          </MenuItem>
                        )}
                        {menuActions.map((menuItem) => {
                          return (
                            <MenuItem key={menuItem.order}>
                              <a
                                onClick={(e) =>
                                  handleMenuCallback(e, menuItem.callBack)
                                }
                                href=""
                              >
                                {menuItem.message}
                              </a>
                            </MenuItem>
                          );
                        })}
                      </Menu>
                    </Dropdown>
                  </li>
                  <li>
                    <Dropdown
                      marginToTarget={0}
                      onShowChange={handlePlusDrpShowChange}
                      placement="bottom-start"
                      show={openPlusDrp}
                      width={200}
                      target={
                        <ActionMenuButton>
                          <Icon type="plus-circle" color={black} />
                        </ActionMenuButton>
                      }
                    >
                      <Menu>
                        <MenuItem>
                          <a
                            data-item-type="folder"
                            onClick={handleCreatingNewItem}
                            href=""
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              paddingInlineStart: '0.5rem',
                              gap: '0.5rem',
                              width: '100%',
                            }}
                          >
                            <LocalIcon
                              iconName={IconLogoFinder({ type: 'folder' })}
                              color={IconColorFinder('folder')}
                            />
                            <span>{t('newFolderAction')}</span>
                          </a>
                        </MenuItem>
                        <MenuDivider />
                        <React.Fragment>
                          <MenuItemUploadFile>
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingInlineStart: '0.5rem',
                                gap: '0.5rem',
                                width: '100%',
                              }}
                            >
                              <LocalIcon
                                iconName={IconLogoFinder({ type: 'document' })}
                                color={IconColorFinder('document')}
                              />
                              <span>{t('uploadAFileAction')}</span>
                              <input
                                type="file"
                                multiple
                                onChange={handleUploadingNewItem}
                              />
                            </span>
                          </MenuItemUploadFile>
                          <MenuItem>
                            <a
                              data-item-type="document"
                              onClick={handleCreatingNewItem}
                              href=""
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingInlineStart: '0.5rem',
                                gap: '0.5rem',
                                width: '100%',
                              }}
                            >
                              <LocalIcon
                                iconName={IconLogoFinder({
                                  type: 'document',
                                  format: 'quill',
                                })}
                                color={blue}
                              />
                              <span>{t('newQuillAction')}</span>
                            </a>
                          </MenuItem>
                          {!(
                            userIsOwnerOrSupport ||
                            managingCompanyId === RastechCompany
                          ) &&
                            (domainFeatures?.allowManageChecklistToUser ||
                              userIsAdvancedAdmin) && (
                              <>
                                <MenuItem>
                                  <a
                                    data-item-type="checklist"
                                    onClick={handleCreatingNewItem}
                                    href=""
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      paddingInlineStart: '0.5rem',
                                      gap: '0.5rem',
                                      width: '100%',
                                    }}
                                  >
                                    <LocalIcon
                                      iconName={IconLogoFinder({
                                        type: 'survey',
                                      })}
                                      color={IconColorFinder('survey')}
                                    />
                                    <span>{t('newChecklistAction')}</span>
                                  </a>
                                </MenuItem>
                              </>
                            )}

                          {(domainFeatures?.preventCreateIndicator &&
                            !userIsOwnerOrSupport) || (
                            <MenuItem>
                              <a
                                data-item-type="indicator"
                                onClick={handleCreatingNewItem}
                                href=""
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  paddingInlineStart: '0.5rem',
                                  gap: '0.5rem',
                                  width: '100%',
                                }}
                              >
                                <LocalIcon
                                  iconName={'survey-shakhes.svg'}
                                  color={IconColorFinder('survey')}
                                />
                                <span>
                                  {
                                    i18n.t('newIndicatorAction', {
                                      ns: 'documentManager',
                                    }) as string
                                  }
                                </span>
                              </a>
                            </MenuItem>
                          )}
                          {(userIsOwnerOrSupport ||
                            managingCompanyId === RastechCompany) && (
                            <>
                              {' '}
                              <MenuItem>
                                <a
                                  data-item-type="survey"
                                  onClick={handleCreatingNewItem}
                                  href=""
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingInlineStart: '0.5rem',
                                    gap: '0.5rem',
                                    width: '100%',
                                  }}
                                >
                                  <LocalIcon
                                    iconName={IconLogoFinder({
                                      type: 'survey',
                                    })}
                                    color={IconColorFinder('survey')}
                                  />
                                  <span>{t('newSurveyAction')}</span>
                                </a>
                              </MenuItem>
                              <MenuItem>
                                <a
                                  data-item-type="link"
                                  onClick={handleCreatingNewItem}
                                  href=""
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingInlineStart: '0.5rem',
                                    gap: '0.5rem',
                                    width: '100%',
                                  }}
                                >
                                  <LocalIcon
                                    iconName={'user.svg'}
                                    color={IconColorFinder('link')}
                                  />
                                  <span>{t('newLinkAction')}</span>
                                </a>
                              </MenuItem>
                            </>
                          )}
                          {(domainFeatures?.allowCreateExternalLink ||
                            userIsOwnerOrSupport) && (
                            <MenuItem>
                              <a
                                data-item-type="externalLink"
                                onClick={handleCreatingNewItem}
                                href=""
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  paddingInlineStart: '0.5rem',
                                  gap: '0.5rem',
                                  width: '100%',
                                }}
                              >
                                <LocalIcon iconName={'link.svg'} color={blue} />
                                <span>{t('newExternalLinkAction')}</span>
                              </a>
                            </MenuItem>
                          )}
                          {(userIsOwnerOrSupport ||
                            managingCompanyId === RastechCompany ||
                            domainFeatures?.allowManageChecklistToUser ||
                            userIsAdvancedAdmin) && (
                            <>
                              <MenuItem>
                                <a
                                  data-item-type="surveyReport"
                                  onClick={handleCreatingNewItem}
                                  href=""
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingInlineStart: '0.5rem',
                                    gap: '0.5rem',
                                    width: '100%',
                                  }}
                                >
                                  <LocalIcon
                                    iconName={IconLogoFinder({
                                      type: 'surveyReport',
                                    })}
                                    color={IconColorFinder('surveyReport')}
                                  />
                                  <span>{t('newReportAction')}</span>
                                </a>
                              </MenuItem>
                              <MenuItem>
                                <a
                                  data-item-type="searchTag"
                                  onClick={handleCreatingNewItem}
                                  href=""
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingInlineStart: '0.5rem',
                                    gap: '0.5rem',
                                    width: '100%',
                                  }}
                                >
                                  <LocalIcon
                                    iconName={IconLogoFinder({
                                      type: 'searchTag',
                                    })}
                                    color={IconColorFinder('searchTag')}
                                  />
                                  <span>{t('newSearchTagAction')}</span>
                                </a>
                              </MenuItem>
                            </>
                          )}
                          <MenuItem>
                            <a
                              data-item-type="itemLink"
                              onClick={handleCreatingNewItem}
                              href=""
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingInlineStart: '0.5rem',
                                gap: '0.5rem',
                                width: '100%',
                              }}
                            >
                              <LocalIcon
                                iconName={'horizontal-link.svg'}
                                color={orange}
                              />
                              <span>{t('addLinkToAnotherDoc')}</span>
                            </a>
                          </MenuItem>
                        </React.Fragment>
                      </Menu>
                    </Dropdown>
                  </li>
                </>
              )}
            </ul>
          </NavHeaderContent>
        </nav>
      </HeaderContainer>
    );
  } else if (dataItem.type === providerItemType.searchTag) {
    return (
      <div style={{ height: '5rem' }} className="flex items-center">
        <nav className="z-depth-0">
          <div className="nav-wrapper">
            <ul
              style={{
                float: 'right',
                fontWeight: 'bold',
                maxWidth: 'calc(100% - ' + actionWidth + ')',
              }}
            >
              <li>
                <span className="text">
                  {dataItem.dadId === null && !dataItem.name ? (
                    <Icon type={'home'} />
                  ) : (
                    dataItem.name
                  )}
                </span>
              </li>
            </ul>
          </div>
        </nav>
      </div>
    );
  } else {
    return <span />;
  }
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = () => {
  return {
    updateFolderIdForSearch: (folderId) =>
      appDispatch(updateSearchInFolderId({ folderId })),
    importContentFromRemote: (
      sourceItemId: number,
      targetItemId: number,
      remoteUrl: string,
      readAccessToken: string,
      forceCopy: boolean,
      excludeSrcFolder: boolean,
      providerId: number
    ) => {
      return appDispatch(
        importContentFromRemote({
          sourceItemId,
          targetItemId,
          remoteUrl,
          readAccessToken,
          forceCopy,
          excludeSrcFolder,
          providerId,
        })
      );
    },
    createProviderItem: (
      providerId: number,
      id: number,
      type: ProviderItemType,
      tags: ITagView[] | undefined
    ) =>
      appDispatch(createNewProviderItem({ providerId, dadId: id, type, tags })),
    creatingProviderItem: (
      providerId: number,
      dadId: number,
      dataUrl: string,
      fileSize: any,
      displayName: string,
      format: any,
      photo: any,
      order: number,
      originalFileName: any,
      error: any
    ) =>
      appDispatch(
        creatingNewProviderItem({
          providerId,
          dadId,
          dataUrl,
          fileSize,
          displayName,
          format,
          photo,
          order,
          originalFileName,
          error,
        })
      ),
  };
};

export default memo(
  connect(undefined, mapDispatchToProps)(FolderHeader)
) as React.FC<OwnProps>;
